import React from 'react';
import PropTypes from 'prop-types';

import { PowerFunnelSurfaces } from '../../Analytics/constants';
import useFableticsCta from '../../hooks/useFableticsCta';
import { USER_STATUS_FREE } from '../../../utils/constants';
import { IMAGES, COPY } from '../../Fabletics/constants';
import FableticsPastDueMessage from './FableticsPastDueMessage';
import useAppleSubscriptionPopup from './useAppleSubscriptionPopup';
import styles from './fableticsUpsellBanner.scss';

// `FREE_HEADLINE` is unique to this creative; the monthly + annual headlines are
// shared with the FableticsSubscriptionsBanner and live in the Fabletics COPY
// constants module.
const FREE_HEADLINE = 'POWER: The Ultimate Upgrade for Your Game, Gear, and Getaways.';

const PowerCard = () => (
    <div className={styles.card} data-testid="fabletics.banner.upsell.power-card">
        <picture className={styles.powerHero}>
            <source media="(max-width: 1024px)" srcSet={IMAGES.powerHeroMobile} />
            <img src={IMAGES.powerHeroDesktop} alt="" className={styles.powerHeroImg} data-testid="fabletics.img-21" />
        </picture>
        <div className={styles.cardBody}>
            <div className={styles.brandRow} data-testid="fabletics.banner.upsell.power-card.wordmark">
                <img src={IMAGES.powerIcon} alt="" className={styles.brandIcon} data-testid="fabletics.img-25" />
                <span className={styles.brandLabelPower} data-testid="fabletics.span-26">POWER</span>
            </div>
            <p className={styles.cardCopy} data-testid="fabletics.p-28">
                {COPY.powerCard}
            </p>
        </div>
    </div>
);

const FableticsCard = () => (
    <div className={styles.card} data-testid="fabletics.banner.upsell.fabletics-card">
        <div className={styles.cardImageWrap}>
            <img src={IMAGES.tennisWomen} alt="" className={`${styles.cardImage} ${styles.cardImageFabletics}`} data-testid="fabletics.img-38" />
        </div>
        <div className={styles.cardBody}>
            <img
                src={IMAGES.fableticsLogo}
                alt="Fabletics"
                className={`${styles.brandLogo} ${styles.brandLogoFabletics}`}
                data-testid="fabletics.banner.upsell.fabletics-card.logo"
            />
            <p className={`${styles.cardCopy} ${styles.cardCopyFabletics}`} data-testid="fabletics.p-47">
                {COPY.fableticsCard}
            </p>
        </div>
    </div>
);

const GpCard = () => (
    <div className={styles.card} data-testid="fabletics.banner.upsell.gp-card">
        <div className={styles.cardImageWrap}>
            <img src={IMAGES.airport} alt="" className={`${styles.cardImage} ${styles.cardImageGp}`} data-testid="fabletics.img-57" />
        </div>
        <div className={styles.cardBody}>
            <img
                src={IMAGES.gpLogo}
                alt="Global Privileges"
                className={`${styles.brandLogo} ${styles.brandLogoGp}`}
                data-testid="fabletics.banner.upsell.gp-card.logo"
            />
            <p className={styles.cardCopy} data-testid="fabletics.p-66">
                {COPY.gpCard}
            </p>
        </div>
    </div>
);

const FableticsUpsellBanner = ({ surface }) => {
    const { open: openAppleSubscriptionPopup, popup: appleSubscriptionPopup } = useAppleSubscriptionPopup();
    const cta = useFableticsCta({
        surface,
        trackImpressions: true,
        onAppleManageClick: openAppleSubscriptionPopup,
    });
    const {
        buttonText,
        handleCtaClick,
        handleLearnMoreClick,
        hasPastDueSubscription,
        userStatus,
    } = cta;

    // Members are routed to FableticsMemberBanner via useFabletics, so this
    // creative only renders for the upsell audience (US Free / Power Monthly).
    const isFree = userStatus === USER_STATUS_FREE;
    const headline = isFree ? FREE_HEADLINE : COPY.monthlyHeadline;

    return (
        <div
            className={styles.banner}
            data-testid="fabletics.banner.upsell.container"
        >
            <p
                className={styles.headline}
                data-testid="fabletics.banner.upsell.headline"
            >
                {headline}
            </p>
            {/* Free desktop: PowerCard, FableticsCard, and GpCard all live in
                the same row. Free mobile: cardRow becomes a flex column, so the
                "Included with the Annual Plan" subhead is rendered inline
                between the Power card and the Fabletics/GP pair (hidden on
                desktop). The ZIP caption follows the same inline pattern for
                monthly mobile only. */}
            <div className={styles.cardRow}>
                {isFree && <PowerCard />}
                {isFree && (
                    <p
                        className={styles.includedSubhead}
                        data-testid="fabletics.banner.upsell.included-subhead"
                    >
                        Included with the Annual Plan
                    </p>
                )}
                <FableticsCard />
                <GpCard />
            </div>
            <div className={styles.ctaSection}>
                <FableticsPastDueMessage
                    hasPastDueSubscription={hasPastDueSubscription}
                    className={styles.pastDueMessage}
                    testId="fabletics.banner.upsell.past-due-message"
                />
                <button
                    type="button"
                    className={styles.primaryCta}
                    data-testid="fabletics.banner.upsell.primary-cta"
                    onClick={handleCtaClick}
                >
                    {buttonText}
                </button>
                <button
                    type="button"
                    className={styles.learnMore}
                    data-testid="fabletics.banner.upsell.learn-more"
                    onClick={handleLearnMoreClick}
                >
                    Learn more
                </button>
            </div>
            {appleSubscriptionPopup}
        </div>
    );
};

FableticsUpsellBanner.propTypes = {
    surface: PropTypes.oneOf([PowerFunnelSurfaces.HOME, PowerFunnelSurfaces.POWER_PERKS_PAGE]),
};

FableticsUpsellBanner.defaultProps = {
    surface: PowerFunnelSurfaces.HOME,
};

export default FableticsUpsellBanner;
